import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AdminService } from './adminApi';
import { AxiosError } from 'axios';

const initialState: any = {
    reconcAreLoading: false,
    reconcIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchReconciles = createAsyncThunk<any, void>(
    'admin/tryToFetchReconciles',
    async () => {
        const result = await AdminService.tryToFetchReconciles();
        return result?.data?.data;
    },
);

export const tryToFetchRules = createAsyncThunk<any, void>(
    'admin/tryToFetchRules',
    async () => {
        const result = await AdminService.tryToFetchRules();
        return result?.data?.data;
    },
);

export const tryToFetchAdminStats = createAsyncThunk<any, void>(
    'admin/tryToFetchAdminStats',
    async () => {
        const result = await AdminService.tryToFetchAdminStats();
        return result?.data;
    },
);

export const tryToFetchExecutiveStats = createAsyncThunk<any, any>(
    'admin/tryToFetchExecutiveStats',
    async (data: any) => {
        const result = await AdminService.tryToFetchExecutiveStats(data);
        return result?.data?.data;
    },
);

export const tryToUpdateRules = createAsyncThunk<any, any>(
    'admin/tryToUpdateRules',
    async (data, { rejectWithValue }) => {
        try {
            const result = await AdminService.tryToUpdateRules(data);
            return result?.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchRuleLogs = createAsyncThunk<any, any>(
    'admin/tryToFetchRuleLogs',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await AdminService.tryToFetchRuleLogs(
            pageNumber,
            pageSize,
            filters,
        );
        return result;
    },
);

export const tryToFetchAdminDiscrepancies = createAsyncThunk<any, any>(
    'admin/tryToFetchAdminDiscrepancies',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await AdminService.tryToFetchAdminDiscrepancies(
            pageNumber,
            pageSize,
            filters,
        );
        return result;
    },
);

export const tryToFetchAdminTemplateFiles = createAsyncThunk<any, any>(
    'admin/tryToFetchAdminTemplateFiles',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await AdminService.tryToFetchAdminTemplateFiles(
            pageNumber,
            pageSize,
            filters,
        );
        return result;
    },
);

export const tryToFetchFailedRecon = createAsyncThunk<any, any>(
    'admin/tryToFetchFailedRecon',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await AdminService.tryToFetchFailedRecon(
            pageNumber,
            pageSize,
            filters,
        );
        return result;
    },
);

export const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchReconciles
            .addCase(tryToFetchReconciles.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchReconciles.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchReconciles.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.reconciliation = action.payload;
            })

            // tryToFetchRules
            .addCase(tryToFetchRules.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchRules.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchRules.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.rules = action.payload;
            })

            // tryToFetchRuleLogs
            .addCase(tryToFetchRuleLogs.pending, (state) => {
                state.logsAreLoading = true;
            })
            .addCase(tryToFetchRuleLogs.rejected, (state) => {
                state.logsAreLoading = false;
            })
            .addCase(tryToFetchRuleLogs.fulfilled, (state, action) => {
                state.logsAreLoading = false;
                state.logs = action.payload;
            })

            // tryToFetchExecutiveStats
            .addCase(tryToFetchExecutiveStats.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchExecutiveStats.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchExecutiveStats.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.executiveStats = action.payload;
            })

            // tryToFetchAdminStats
            .addCase(tryToFetchAdminStats.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchAdminStats.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchAdminStats.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.adminStats = action.payload;
            })

            // tryToFetchAdminDiscrepancies
            .addCase(tryToFetchAdminDiscrepancies.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchAdminDiscrepancies.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchAdminDiscrepancies.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.adminDiscrepancies = action.payload;
            })

            // tryToFetchAdminTemplateFiles
            .addCase(tryToFetchAdminTemplateFiles.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchAdminTemplateFiles.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchAdminTemplateFiles.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.adminTemplateFiles = action.payload;
            })

            // tryToFetchFailedRecon
            .addCase(tryToFetchFailedRecon.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchFailedRecon.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchFailedRecon.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                state.failedRecon = action.payload;
            })
    },
});

export default adminSlice.reducer;